import React, { useState } from "react";
import { Card, Box, Paper, Typography, Collapse } from "@material-ui/core";
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import NoteIcon from "@material-ui/icons/Note";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VideocamIcon from "@material-ui/icons/Videocam";
import SubActivityService from "./Locality/Service/subActivityService";
import StudentCards from "./UserProfileCard";
import { useEffect } from 'react';
import ActivityService from "./Locality/Service/activityService";
import SuperActivityService from "./Locality/Service/superActivityService";
import StudentService from "./Locality/Service/studentService";
const isPublishedActivityRecord = (record) =>
    String(record?.publicStatus || "").toLowerCase() === "publish";

const getPublishedRecords = (entry) => {
    const records = Array.isArray(entry?.activityRecordsId) ? entry.activityRecordsId : [];
    return records.filter(isPublishedActivityRecord);
};

const STATUS_KEYS = ["P", "WA", "WI", "M"];

const normalizeActivityKey = (key) => String(key ?? "").trim();

const getActivityKeyIndex = (key) => {
    const normalized = normalizeActivityKey(key);
    if (!normalized) return -1;
    return STATUS_KEYS.indexOf(normalized);
};

const isStatusVisibleForRecord = (record, statusCode) => {
    if (!record) return false;
    const currentIndex = getActivityKeyIndex(record.key);
    if (currentIndex < 0) return false;
    const statusIndex = STATUS_KEYS.indexOf(statusCode);
    if (statusIndex < 0) return false;
    return statusIndex <= currentIndex;
};

const getStatusHistoryEntry = (record, statusCode) => {
    if (!record || !isStatusVisibleForRecord(record, statusCode)) return null;
    return record.statusHistory?.find((item) => item.key === statusCode) || null;
};

const hasDisplayableStatus = (record) => {
    if (!Array.isArray(record) || record.length === 0) return false;
    const published = getPublishedActivityRecord(record);
    if (!published) return false;
    if (getActivityKeyIndex(published.key) < 0) return false;
    const history = published.statusHistory;
    if (!Array.isArray(history)) return false;
    return history.some(
        (item) =>
            STATUS_KEYS.includes(item?.key)
            && item?.changedAt
            && isStatusVisibleForRecord(published, item.key)
    );
};

const getPublishedActivityRecord = (records) => {
    if (!Array.isArray(records) || records.length === 0) return null;
    const published = records.find(isPublishedActivityRecord);
    return published || null;
};

const getActivityImages = (record) => {
    if (!record) return [];
    if (Array.isArray(record.imageURL)) return record.imageURL.filter(Boolean);
    return record.imageURL ? [record.imageURL] : [];
};

const getActivityVideos = (record) => {
    if (!record) return [];
    if (Array.isArray(record.videoURL)) return record.videoURL.filter(Boolean);
    return record.videoURL ? [record.videoURL] : [];
};

const hasActivityMedia = (record) =>
    getActivityImages(record).length > 0 || getActivityVideos(record).length > 0;

const getActivityRemarks = (record) => (record?.remarks || "").trim();

const hasActivityData = (result = []) =>
    result.some(
        (activity) =>
            Array.isArray(activity?.activityList) &&
            activity.activityList.some((entry) => getPublishedRecords(entry).length > 0)
    );

const useStyles = makeStyles((theme) => ({
    pageWrapper: {
        padding: theme.spacing(2.5),
        background: "#f8fafc",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1.5),
        },
    },
    selectorCard: {
        borderRadius: 22,
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(2.5),
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
    },
    contentCard: {
        borderRadius: 24,
        padding: theme.spacing(3),
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2),
            borderRadius: 18,
        },
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
        },
    },
    titleBlock: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.75),
    },
    title: {
        fontWeight: 800,
        fontSize: "1.85rem",
        color: "#0f172a",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.4rem",
        },
    },
    subtitle: {
        color: "#64748b",
        maxWidth: 620,
        lineHeight: 1.6,
        fontSize: "0.95rem",
    },
    actionButton: {
        borderRadius: 12,
        padding: theme.spacing(1.1, 2.2),
        textTransform: "none",
        fontWeight: 700,
        background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
        color: "#ffffff",
        boxShadow: "0 10px 22px rgba(37, 99, 235, 0.24)",
        "&:hover": {
            background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    tabsScroller: {
        overflowX: "auto",
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    tabsWrap: {
        display: "inline-flex",
        gap: theme.spacing(1.25),
        minWidth: "100%",
        [theme.breakpoints.down("sm")]: {
            gap: theme.spacing(1),
        },
    },
    tabButton: {
        border: "1px solid #cbd5e1",
        background: "#ffffff",
        color: "#334155",
        fontWeight: 700,
        fontSize: "0.9rem",
        borderRadius: 14,
        padding: theme.spacing(1.15, 2),
        whiteSpace: "nowrap",
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
            borderColor: "#60a5fa",
            backgroundColor: "#eff6ff",
            color: "#1d4ed8",
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1, 1.5),
            fontSize: "0.82rem",
            borderRadius: 12,
        },
    },
    tabButtonActive: {
        background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
        color: "#1d4ed8",
        borderColor: "#60a5fa",
        boxShadow: "0 8px 18px rgba(59, 130, 246, 0.18)",
    },
    tableWrapper: {
        overflowX: "auto",
        borderRadius: 18,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
    },
    groupContainer: {
        borderRadius: 18,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        overflow: "hidden",
    },
    groupHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.6, 2),
        background: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        cursor: "pointer",
        transition: "background 0.2s ease",
        "&:hover": {
            background: "#eff6ff",
        },
    },
    groupHeaderActive: {
        background: "#eff6ff",
    },
    groupHeaderLeft: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
    },
    groupChevron: {
        color: "#1d4ed8",
        fontSize: "1.4rem",
        flexShrink: 0,
    },
    groupTitle: {
        fontWeight: 800,
        fontSize: "1rem",
        color: "#0f172a",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    groupCount: {
        color: "#64748b",
        fontWeight: 600,
        fontSize: "0.85rem",
        whiteSpace: "nowrap",
        flexShrink: 0,
    },
    table: {
        width: "100%",
        minWidth: 760,
        borderCollapse: "separate",
        borderSpacing: 0,
    },
    headCell: {
        background: "#eff6ff",
        color: "#1e3a8a",
        fontWeight: 800,
        fontSize: "0.9rem",
        padding: theme.spacing(1.75, 1.25),
        textAlign: "center",
        borderBottom: "1px solid #dbeafe",
        position: "sticky",
        top: 0,
        zIndex: 1,
    },
    firstHeadCell: {
        textAlign: "left",
        minWidth: 220,
    },
    secondHeadCell: {
        textAlign: "left",
        minWidth: 220,
    },
    row: {
        "&:nth-child(even) td": {
            backgroundColor: "#fcfdff",
        },
        "&:hover td": {
            backgroundColor: "#f8fbff",
        },
    },
    activityCell: {
        padding: theme.spacing(1.4, 1.25),
        borderBottom: "1px solid #e5e7eb",
        fontWeight: 700,
        color: "#0f172a",
        backgroundColor: "#f8fafc",
        minWidth: 220,
    },
    subActivityCell: {
        padding: theme.spacing(1.4, 1.25),
        borderBottom: "1px solid #e5e7eb",
        color: "#334155",
        minWidth: 220,
    },
    statusCell: {
        padding: theme.spacing(1.2, 1),
        borderBottom: "1px solid #e5e7eb",
        textAlign: "center",
        fontWeight: 700,
        minWidth: 120,
        fontSize: "0.88rem",
    },
    inactiveStatus: {
        color: "#94a3b8",
    },
    clickableStatus: {
        cursor: "pointer",
    },
    statusCellActive: {
        boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.65)",
    },
    detailRow: {
        backgroundColor: "#f8fafc",
        "& td": {
            borderBottom: "1px solid #e6eaf0",
            paddingTop: theme.spacing(1.5),
            paddingBottom: theme.spacing(1.5),
        },
    },
    detailCell: {
        padding: theme.spacing(0, 1.5, 1.5),
    },
    detailStack: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.25),
    },
    noteExpandBox: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 10,
        border: "1px solid #e6eaf0",
        backgroundColor: "#ffffff",
    },
    detailMeta: {
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "#9aa6b6",
        textTransform: "uppercase",
        marginBottom: 4,
    },
    observationText: {
        color: "#0f2d4d",
        fontSize: "0.9rem",
        lineHeight: 1.65,
        whiteSpace: "pre-wrap",
    },
    imageGrid: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1.25),
        marginTop: theme.spacing(0.5),
    },
    imageCard: {
        position: "relative",
        borderRadius: 10,
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 6px 16px rgba(15, 23, 42, 0.1)",
        },
    },
    detailImage: {
        width: 88,
        height: 88,
        objectFit: "cover",
        display: "block",
        cursor: "pointer",
    },
    detailVideo: {
        width: 88,
        height: 88,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#0f2d4d",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": {
            backgroundColor: "#1769d6",
        },
    },
    imageIndex: {
        position: "absolute",
        top: 6,
        left: 6,
        backgroundColor: "rgba(15, 23, 42, 0.72)",
        color: "#ffffff",
        fontSize: "0.68rem",
        fontWeight: 700,
        borderRadius: 6,
        padding: "2px 6px",
    },
    loadingBox: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: "#2563eb",
        fontWeight: 700,
    },
    emptyState: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: "#64748b",
        fontWeight: 600,
    },
    helperText: {
        color: "#64748b",
        fontSize: "0.85rem",
        marginBottom: theme.spacing(1.5),
    },
}));

export default function ParentActivityTrackerTable() {
    const classes = useStyles();

    const [activityList, setActivityList] = useState([]);
    const [studentsList, setstudentsList] = useState([]);
    const [subActivityList, setSubActivityList] = useState([]);
    var [classId, setClassId] = useState(null);
    const [addSuperActivityList, setAddSuperActivityList] = useState([]);
    const [studentList, setStudentList] = useState([]);
    var [isLoading, setIsLoading] = useState(false);
    const [addClassList, setAddClassList] = useState([]);
    var [studentId, setStudentId] = useState("");

    const [activeTab, setActiveTab] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [expandedKeyDetail, setExpandedKeyDetail] = useState(null);

    const toggleKeyDetail = (rowKey, statusCode, hasStatusDate, publishedRecord) => {
        if (!hasStatusDate || !publishedRecord) return;
        const remarks = getActivityRemarks(publishedRecord);
        if (!remarks && !hasActivityMedia(publishedRecord)) return;

        setExpandedKeyDetail((prev) =>
            prev?.rowKey === rowKey && prev?.statusCode === statusCode
                ? null
                : { rowKey, statusCode }
        );
    };

    const toggleGroup = (groupId) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };
    useEffect(() => {
        // getAddClassList();
        getSuperActivityList();
        fetchStudentList();

        return () => {
            setStudentList([]);
            setAddSuperActivityList([]);
            setActivityList([]);
            setSubActivityList([]);
        }
    }, []);

    useEffect(() => {
        if (!studentId && studentList.length > 0) {
            const firstStudent = studentList[0];
            const firstStudentId = firstStudent?._id || "";
            const firstClassId = firstStudent?.classId?._id || firstStudent?.classId || null;

            if (firstStudentId && firstClassId) {
                setStudentId(firstStudentId);
                setClassId(firstClassId);
                getSuperActivityList(firstStudentId, firstClassId);
            }
        }
    }, [studentList, studentId]);
    const colorMap = {
        "P": "#5C9882",
        "WI": "#E6B163",
        "WA": "#A75C9C",
        "M": '#147ad482'
    };
    const onSubmit = (data, studentsId, classsId) => {
        setIsLoading(true); // Start loading

        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        const resolvedStudentId = studentsId ? studentsId : studentId;
        const resolvedClassId = classsId ? classsId : classId;

        const keys = {
            schooleId: userDetails?.schooleId?._id || userDetails?.schooleId || userDetails?.schoolId?._id || userDetails?.schoolId,
            isFuturePlanning: false,
            isInitLoad: true,
            superActivityId: data,
            studentId: resolvedStudentId,
            classId: resolvedClassId,
            startDate: "",
            endDate: "",
            status: true,
            publicStatus: "publish",
        };

        ActivityService.findActivityReports(keys)
            .then((result) => {
                const safeResult = result || [];
                setActivityList(safeResult);
                setExpandedKeyDetail(null);

                const allStudents = [];
                safeResult.forEach(activity =>
                    activity.activityList.forEach(rec => {
                        const student = rec.studentId;
                        if (student && !allStudents.find(s => s._id === student._id)) {
                            allStudents.push(student);
                        }
                    })
                );
                setstudentsList(allStudents);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setIsLoading(false); // End loading
            });
    };

    const getSubActivityMasterList = async (selectedClassId) => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        const schoolId = userDetails.schooleId || userDetails.schoolId;

        if (!schoolId) {
            setSubActivityList([]);
            return;
        }

        try {
            const res = await SubActivityService.getAllSubActivity(schoolId);
            const filtered = (res || []).filter((item) => {
                const itemClassId = item?.classId?._id || item?.classId;
                return !selectedClassId || itemClassId === selectedClassId;
            });
            setSubActivityList(filtered);
        } catch (err) {
            console.error(err);
            setSubActivityList([]);
        }
    };


    const getSuperActivityList = async (studentId, classId) => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        try {
            const schoolId = userDetails.schooleId || userDetails.schoolId;
            if (!schoolId || !classId) {
                setAddSuperActivityList([]);
                setActivityList([]);
                return;
            }

            getSubActivityMasterList(classId);
            const res = await SuperActivityService.getAllSuperActivityById(schoolId, { classId: classId });
            setAddSuperActivityList(res);

            if (res.length > 0) {
                setActiveTab(res[0]._id);
                onSubmit(res[0]._id, studentId, classId);
            }
        } catch (err) {
            console.error(err);
        }
    };
    const fetchStudentList = () => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        const body = {};

        if (userDetails.role === "PARENT") {
            body.userId = userDetails._id;
        } else if (userDetails.role === "SCHOOLE" || userDetails.role === "SUPERADMIN") {
            body.schooleId = userDetails.schooleId || userDetails.schoolId;
        } else if (userDetails.role === "TEACHER") {
            body.schooleId = userDetails.schooleId || userDetails.schoolId;
            if (userDetails.classId) body.classId = userDetails.classId;
            if (userDetails.sectionId) body.sectionId = userDetails.sectionId;
        }

        StudentService.findStudentList(body)
            .then((res) => {
                const mappedStudents = (res || []).map((student) => ({
                    ...student,
                    fullName: `${student.firstName || ""} ${student.lastName || ""}`.trim(),
                }));
                setStudentList(mappedStudents);

                if (mappedStudents.length > 0) {
                    setStudentId(mappedStudents[0]._id);
                    setClassId(mappedStudents[0].classId?._id || mappedStudents[0].classId);
                    //  onSubmit(mappedStudents[0]._id, mappedStudents[0].classId._id);
                    getSuperActivityList(mappedStudents[0]._id, mappedStudents[0].classId?._id || mappedStudents[0].classId);
                }

            })
            .catch((err) => console.error(err));
    };
    const handleTabClick = (superActivityId) => {

        setActiveTab(superActivityId);
        onSubmit(superActivityId, studentId, classId);
    };
    const handleStudentClick = (studentId, classId) => {
        setStudentId(studentId);
        setClassId(classId);
        getSuperActivityList(studentId, classId);
        // You can now fetch the student's assignments or do anything with this ID
    };
    const getStudentReportList = () => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail"));

        if (studentId === '') {
            alert("Please Select Student");
            return;
        }
        const getstudentId = { "studentId": studentId, };

        SubActivityService.getStudentReport(getstudentId).then((res) => {
            // const blob = new Blob(res.pdfBuffer);
            const buffer = Buffer.from(res.pdfBuffer);
            const blob = new Blob([buffer]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.style = "display: none";
            a.href = url;
            a.download = "studentreport.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
            // const blob = new Blob([res.pdfBuffer]);
            // saveAs(blob, 'filename.pdf');
            // const file = new File(res.pdfBuffer, 'filename.txt', { type: 'text/plain' });
            // setStudentReportList(res);
        }).catch((err) => {
            alert(err.response.data.message)
        });
    }
    return (
        <Box className={classes.pageWrapper}>
            {studentList.length > 1 && (
                <Card className={classes.selectorCard}>
                    <StudentCards
                        studentList={studentList}
                        onStudentSelect={handleStudentClick}
                        selectedStudentId={studentId}
                    />
                </Card>
            )}

            <Paper className={classes.contentCard}>
                <Box className={classes.headerRow}>
                    <Box className={classes.titleBlock}>
                        <Typography className={classes.title}>Area Of Work</Typography>
                        <Typography className={classes.subtitle}>
                            Track the student&apos;s learning progress across each Montessori work area
                            with a cleaner layout that also works better on small screens.
                        </Typography>
                    </Box>
                    {/* <Button className={classes.actionButton} onClick={getStudentReportList}>
                        Download Report
                    </Button> */}
                </Box>

                <Typography className={classes.helperText}>
                    Tap an area tab to switch categories. On small screens, swipe horizontally to view the full progress table.
                </Typography>

                <Box className={classes.tabsScroller}>
                    <Box className={classes.tabsWrap}>
                        {addSuperActivityList.map((area) => {
                            const isActive = activeTab === area._id;
                            return (
                                <button
                                    key={area._id}
                                    type="button"
                                    onClick={() => handleTabClick(area._id)}
                                    className={`${classes.tabButton} ${isActive ? classes.tabButtonActive : ""}`}
                                >
                                    {area.superActivityName}
                                </button>
                            );
                        })}
                    </Box>
                </Box>

                {studentId && !isLoading && (() => {
                    const STATUS_LABELS = {
                        P: "Presented",
                        WA: "Works with Assistance",
                        WI: "Works Independently",
                        M: "Mastered",
                    };

                    const renderStatusCell = (statusCode, exercise, rowKey) => {
                        const publishedRecord = getPublishedActivityRecord(exercise.record);
                        const activityDetails = publishedRecord
                            ? getStatusHistoryEntry(publishedRecord, statusCode)
                            : null;

                        const hasStatusDate = Boolean(activityDetails?.changedAt);
                        const remarks = getActivityRemarks(publishedRecord);
                        const images = getActivityImages(publishedRecord);
                        const videos = getActivityVideos(publishedRecord);
                        const hasDetailContent = Boolean(remarks || hasActivityMedia(publishedRecord));
                        const isClickable = hasStatusDate && hasDetailContent && publishedRecord;
                        const isDetailOpen =
                            expandedKeyDetail?.rowKey === rowKey &&
                            expandedKeyDetail?.statusCode === statusCode;
                        const bgColor = colorMap[statusCode];

                        return (
                            <td
                                className={`${classes.statusCell} ${!activityDetails ? classes.inactiveStatus : ""} ${isClickable ? classes.clickableStatus : ""} ${isDetailOpen ? classes.statusCellActive : ""}`}
                                style={{
                                    backgroundColor: activityDetails ? bgColor : undefined,
                                    color: activityDetails ? "#ffffff" : undefined,
                                }}
                                onClick={() => {
                                    if (isClickable) {
                                        toggleKeyDetail(rowKey, statusCode, hasStatusDate, publishedRecord);
                                    }
                                }}
                                title={isClickable ? "View observation and media" : undefined}
                            >
                                {activityDetails && activityDetails.changedAt
                                    ? new Date(activityDetails.changedAt).toLocaleDateString("en-GB")
                                    : "-"}
                            </td>
                        );
                    };

                    const renderKeyDetailRow = (exercise, rowKey, statusCode) => {
                        const publishedRecord = getPublishedActivityRecord(exercise.record);
                        if (!publishedRecord) return null;

                        const remarks = getActivityRemarks(publishedRecord);
                        const images = getActivityImages(publishedRecord);
                        const videos = getActivityVideos(publishedRecord);
                        const statusLabel = STATUS_LABELS[statusCode] || statusCode;
                        const statusEntry = getStatusHistoryEntry(publishedRecord, statusCode);
                        const statusDate = statusEntry?.changedAt
                            ? new Date(statusEntry.changedAt).toLocaleDateString("en-GB")
                            : "";
                        const exerciseName = exercise?.name || "Activity";
                        const statusMeta = statusDate ? `${statusLabel} · ${statusDate}` : statusLabel;

                        return (
                            <tr key={`${rowKey}-${statusCode}-detail`} className={classes.detailRow}>
                                <td className={classes.detailCell} colSpan={5}>
                                    <Box className={classes.detailStack}>
                                        {remarks && (
                                            <Box className={classes.noteExpandBox}>
                                                <NoteIcon style={{ color: "#1769d6", marginTop: 2, flexShrink: 0 }} />
                                                <Box flex={1}>
                                                    <Typography className={classes.detailMeta}>
                                                        Observation · {exerciseName} · {statusMeta}
                                                    </Typography>
                                                    <Typography className={classes.observationText}>
                                                        {remarks}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}

                                        {images.length > 0 && (
                                            <Box className={classes.noteExpandBox}>
                                                <PhotoLibraryIcon style={{ color: "#1769d6", marginTop: 2, flexShrink: 0 }} />
                                                <Box flex={1}>
                                                    <Typography className={classes.detailMeta}>
                                                        Images · {exerciseName} · {statusMeta}
                                                    </Typography>
                                                    <Box className={classes.imageGrid}>
                                                        {images.map((url, idx) => (
                                                            <Box key={`${rowKey}-img-${idx}`} className={classes.imageCard}>
                                                                <span className={classes.imageIndex}>{idx + 1}</span>
                                                                <img
                                                                    src={url}
                                                                    alt={`${exerciseName} ${idx + 1}`}
                                                                    className={classes.detailImage}
                                                                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                                                />
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}

                                        {videos.length > 0 && (
                                            <Box className={classes.noteExpandBox}>
                                                <VideocamIcon style={{ color: "#1769d6", marginTop: 2, flexShrink: 0 }} />
                                                <Box flex={1}>
                                                    <Typography className={classes.detailMeta}>
                                                        Videos · {exerciseName} · {statusMeta}
                                                    </Typography>
                                                    <Box className={classes.imageGrid}>
                                                        {videos.map((url, idx) => (
                                                            <Box key={`${rowKey}-vid-${idx}`} className={classes.imageCard}>
                                                                <span className={classes.imageIndex}>{idx + 1}</span>
                                                                <Box
                                                                    className={classes.detailVideo}
                                                                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                            e.preventDefault();
                                                                            window.open(url, "_blank", "noopener,noreferrer");
                                                                        }
                                                                    }}
                                                                    aria-label={`Play video ${idx + 1}`}
                                                                >
                                                                    <PlayArrowIcon style={{ color: "#ffffff", fontSize: 36 }} />
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </td>
                            </tr>
                        );
                    };

                    const groups = (activityList || []).map((activity) => {
                        const matchingEntries = (activity.activityList || []).filter((item) => {
                            const recordStudentId = item?.studentId?._id || item?.studentId;
                            return (
                                recordStudentId === studentId &&
                                getPublishedRecords(item).length > 0
                            );
                        });
                        const exerciseMasterList = subActivityList.filter((item) => {
                            const subActivityParentId = item?.activityId?._id || item?.activityId;
                            return String(subActivityParentId) === String(activity.id);
                        });

                        const recordedExercises = matchingEntries
                            .map((entry) => {
                                const subActivity = getPublishedRecords(entry)[0]?.subActivityId;
                                if (!subActivity) return null;
                                return {
                                    id: subActivity?._id || subActivity,
                                    name: subActivity?.subActivityName || "-",
                                };
                            })
                            .filter(Boolean);

                        const exerciseRows = exerciseMasterList.length > 0
                            ? exerciseMasterList.map((item) => ({
                                id: item?._id,
                                name: item?.subActivityName || "-",
                            }))
                            : recordedExercises.length > 0
                                ? recordedExercises
                                : [{ id: null, name: "-" }];

                        const exercises = exerciseRows.map((exercise) => {
                            const matchedEntry = matchingEntries.find((entry) => {
                                const subActivity = getPublishedRecords(entry)[0]?.subActivityId;
                                const subActivityId = subActivity?._id || subActivity || null;
                                return String(subActivityId) === String(exercise.id);
                            }) || (exercise.id === null ? matchingEntries[0] : null);

                            const record = matchedEntry ? getPublishedRecords(matchedEntry) : undefined;
                            return { ...exercise, record };
                        });

                        const performedCount = exercises.filter(
                            (exercise) => hasDisplayableStatus(exercise.record)
                        ).length;

                        return {
                            id: activity.id,
                            activityName: activity.activityName,
                            exercises,
                            performedCount,
                        };
                    });

                    if (groups.length === 0) {
                        return (
                            <Box className={classes.emptyState}>
                                No activity records available for the selected student.
                            </Box>
                        );
                    }

                    return (
                        <Box className={classes.groupContainer}>
                            {groups.map((group, groupIndex) => {
                                const groupKey = group.id ?? `group-${groupIndex}`;
                                const isOpen = !!expandedGroups[groupKey];
                                return (
                                    <Box key={groupKey}>
                                        <Box
                                            className={`${classes.groupHeader} ${isOpen ? classes.groupHeaderActive : ""}`}
                                            onClick={() => toggleGroup(groupKey)}
                                        >
                                            <Box className={classes.groupHeaderLeft}>
                                                {isOpen ? (
                                                    <KeyboardArrowDownIcon className={classes.groupChevron} />
                                                ) : (
                                                    <KeyboardArrowRightIcon className={classes.groupChevron} />
                                                )}
                                                <Typography className={classes.groupTitle}>
                                                    {group.activityName}
                                                </Typography>
                                            </Box>
                                            <Typography className={classes.groupCount}>
                                                {group.performedCount} {group.performedCount === 1 ? "exercise performed" : "exercises performed"}
                                            </Typography>
                                        </Box>

                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            <Box className={classes.tableWrapper} style={{ borderRadius: 0, border: "none" }}>
                                                <table className={classes.table}>
                                                    <thead>
                                                        <tr>
                                                            <th className={`${classes.headCell} ${classes.firstHeadCell}`}>Exercise</th>
                                                            <th className={classes.headCell}>Presented (P)</th>
                                                            <th className={classes.headCell}>Works with Assistance (WA)</th>
                                                            <th className={classes.headCell}>Works Independently (WI)</th>
                                                            <th className={classes.headCell}>Mastered (M)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {group.exercises.flatMap((exercise, index) => {
                                                            const rowKey = `${groupKey}-${exercise?.id || index}`;
                                                            const isDetailOpen = expandedKeyDetail?.rowKey === rowKey;
                                                            const openStatusCode = isDetailOpen
                                                                ? expandedKeyDetail?.statusCode
                                                                : null;

                                                            return [
                                                                <tr key={rowKey} className={classes.row}>
                                                                    <td className={classes.subActivityCell}>
                                                                        {exercise?.name || "-"}
                                                                    </td>
                                                                    {renderStatusCell("P", exercise, rowKey)}
                                                                    {renderStatusCell("WA", exercise, rowKey)}
                                                                    {renderStatusCell("WI", exercise, rowKey)}
                                                                    {renderStatusCell("M", exercise, rowKey)}
                                                                </tr>,
                                                                openStatusCode
                                                                    ? renderKeyDetailRow(exercise, rowKey, openStatusCode)
                                                                    : null,
                                                            ].filter(Boolean);
                                                        })}
                                                    </tbody>
                                                </table>
                                            </Box>
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                })()}

                {isLoading && (
                    <Box className={classes.loadingBox}>Loading activity progress...</Box>
                )}
            </Paper>
        </Box>
    );
}
